import { useState, useEffect } from 'react';
import { fetchTesseractStaffList, type TesseractStaffEntry } from '../services/tesseractStaffApi';
import { usernameCacheService } from '../services/usernameCache';
import { filterStaffByQuery } from '../utils/staffSearch';

export interface UseStaffListSearchOptions {
  debouncedQuery: string;
  enabled: boolean;
  /** Minimum query length before searching (default 2). */
  minLength?: number;
  maxResults?: number;
}

/**
 * Loads the public staff roster, resolves usernames, then filters client-side
 * by username or Minecraft UUID (same UX as Elasticsearch player pickers).
 */
export function useStaffListSearch({
  debouncedQuery,
  enabled,
  minLength = 2,
  maxResults = 10,
}: UseStaffListSearchOptions): {
  matches: TesseractStaffEntry[];
  searching: boolean;
  error: string | null;
} {
  const [matches, setMatches] = useState<TesseractStaffEntry[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || debouncedQuery.length < minLength) {
      setMatches([]);
      setSearching(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setSearching(true);

    (async () => {
      try {
        const staff = await fetchTesseractStaffList();
        await usernameCacheService.ensureUsernames(staff.map((s) => s.minecraft_uuid));
        if (cancelled) return;
        const filtered = filterStaffByQuery(staff, debouncedQuery, (u) =>
          usernameCacheService.getName(u)
        );
        setMatches(filtered.slice(0, maxResults));
        setError(null);
      } catch (e) {
        if (!cancelled) {
          setMatches([]);
          setError(e instanceof Error ? e.message : 'Failed to load staff list');
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, enabled, minLength, maxResults]);

  return { matches, searching, error };
}
